import { useAtom, useAtomValue, useStore as useJotaiStore } from 'jotai';
import { useCallback } from 'react';

import {
  CONNECTION_STATUS,
  type ConnectionStatus,
  cancelReconnectPendingAtom,
  chronicleConnectionContextAtom,
  chronicleDeviceStateAtom,
  manualReconnectPendingAtom,
} from '@/atoms/chronicle';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { chronicleBridge } from '@/tauri/chronicle';
import { logHardwareDatadogEvent } from '@/utils/analytics/log-hardware-datadog-event';
import { trackHardwareRetryConnectionTapped } from '@/utils/analytics/track-hardware-events';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';

import { track } from '@/lib/analytics/track';
import { useBluetoothCheck } from './useBluetoothCheck';
import { useChronicleDeviceStatus } from './useChronicleDeviceStatus';
import { useChronicleErrorModal } from './useChronicleErrorModal';
import { useChronicleStateHelpers } from './useChronicleStateHelpers';
import {
  getOrGenerateBindingCode,
  pushSignedInChronicleUser,
  syncBindingCodeToServer,
} from './utils/bindingCode';
import { parseConnectionError, toChronicleError } from './utils/parseConnectionError';

// Client-side backstop so the button can't hang forever. It deliberately does
// NOT try to outlast the true worst case (~125s: a forceReconnect can queue
// behind an in-flight monitor reconnect arm on the single-lane actor, then run
// its own ~80s arm — the Rust IPC reply budget is 135s). That queue-wait is
// handled by reconciling against the mirrored Rust status in the catch branch:
// a late reply while Rust still shows progress is suppressed there, so this
// timeout only needs to outlast a lone stalled arm.
const MANUAL_RECONNECT_TIMEOUT_MS = 85_000;
const MANUAL_RECONNECT_TIMEOUT_ERROR = 'BLE_CONNECT_TIMEOUT';

// Statuses where the mirrored Rust state shows the connect is still making
// progress, so a manual-reconnect timeout is a late IPC reply (queue-wait
// behind another actor arm), not a failure — surfacing an error or tearing the
// scan down would be a false negative. RECONNECTING is the status Rust mirrors
// while a queued forceReconnect waits behind a monitor `attempt_reconnect` arm.
const RECONNECT_IN_PROGRESS_STATUSES: readonly ConnectionStatus[] = [
  CONNECTION_STATUS.CONNECTING,
  CONNECTION_STATUS.CONNECTED,
  CONNECTION_STATUS.RECONNECTING,
];

// Failure causes the background arm can genuinely retry through — a terminal
// modal for these while the pill is spinning would contradict live recovery.
// Anything else (binding mismatch, permissions) needs the user to act, so the
// modal still owns it even when the arm keeps running.
const ARM_RECOVERABLE_ERROR_CODES: readonly string[] = [
  'BLUETOOTH_DISABLED',
  'DEVICE_NOT_FOUND',
  'CONNECTION_TIMEOUT',
  'CONNECTION_FAILED',
];

function isManualReconnectTimeout(error: unknown): boolean {
  return error instanceof Error && error.message === MANUAL_RECONNECT_TIMEOUT_ERROR;
}

async function withReconnectTimeout<T>(promise: Promise<T>): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(MANUAL_RECONNECT_TIMEOUT_ERROR));
    }, MANUAL_RECONNECT_TIMEOUT_MS);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

/**
 * Hook for managing an already-paired Chronicle device.
 *
 * Provides actions for:
 * - Reconnect: delegates to Rust `auto_connect` (5s scan with early exit + BLE connect)
 * - Disconnect: BLE disconnect only (preserves pairing and backend link)
 *
 * For device removal, use `useRemoveDevice` instead.
 * Used by DeviceCard. For first-time pairing, use `useChroniclePairing`.
 */
export function useChronicleConnect() {
  const [deviceState] = useAtom(chronicleDeviceStateAtom);
  const connectionContext = useAtomValue(chronicleConnectionContextAtom);
  const { isScanning, isConnecting: isConnectingStatus } = useChronicleDeviceStatus();
  const { checkBluetoothStatus } = useBluetoothCheck();
  const { setErrorWithModal } = useChronicleErrorModal();
  const {
    clearAutoReconnect,
    setConnectionStatus,
    markManualDisconnect,
    setDeviceState,
  } = useChronicleStateHelpers();

  const isConnecting = isScanning || isConnectingStatus;

  // APP-4895 (Phase C): single source of truth for "manual reconnect IPC
  // in-flight" lives in `manualReconnectPendingAtom`. Synchronous reads
  // via `jotaiStore.get` serve as the same-tick double-click guard
  // (writes are synchronous, so a second click in the same tick sees
  // `true` immediately — no `useRef` needed). Same atom is consumed by
  // `reconnectButtonStateAtom` so the UI stays disabled for the full IPC
  // window, regardless of intermediate Rust state-event timing.
  const jotaiStore = useJotaiStore();

  const reconnect = useCallback(async () => {
    // Track every tap (including ones swallowed by the in-flight guard) so
    // we don't lose visibility into double-click frequency.
    trackHardwareRetryConnectionTapped();

    if (jotaiStore.get(manualReconnectPendingAtom)) {
      return;
    }
    jotaiStore.set(manualReconnectPendingAtom, true);

    try {
      const bluetoothCheck = await checkBluetoothStatus();
      if (!bluetoothCheck.ok) {
        setErrorWithModal(bluetoothCheck.error);
        return;
      }

      clearAutoReconnect();

      try {
        setConnectionStatus(CONNECTION_STATUS.SCANNING);

        track({
          event_name: TrackingEventName.HardwareDeviceScanStarted,
          props: { source: 'settings' },
        });

        let bindingCode: string | undefined;
        try {
          const binding = await getOrGenerateBindingCode();
          if (!binding.isNew) {
            bindingCode = binding.code;
          }
        } catch (error) {
          logger.warn({
            msg: 'Binding code fetch failed before reconnect, Rust will use the stored code',
            error: error instanceof Error ? error.message : String(error),
          });
        }

        // Ordered ahead of the reconnect for the same reason as startup: the
        // actor resolves the stored target per account, and drops writes it
        // cannot attribute — a reconnect that lands with no account stores
        // neither the confirmed code nor the peripheral UUID.
        if (!(await pushSignedInChronicleUser())) {
          throw new Error('no_signed_in_account');
        }

        const { confirmedBindingCode } = await withReconnectTimeout(
          // APP-4861: `forceReconnect` is the user-initiated IPC — Rust stops
          // the `DeviceMonitor` retry loop and runs a fresh attempt. It does
          // not preempt a monitor `attempt_reconnect` already running on the
          // single-lane actor, so it can queue behind one (see
          // MANUAL_RECONNECT_TIMEOUT_MS). Replaces the previous `autoConnect`
          // path which silently no-oped while the monitor was retrying (a
          // contributing cause of the APP-4779 / APP-4693 dead-button class).
          chronicleBridge.forceReconnect({
            bindingCode,
            // Tier-2 name matching is exact, and the bridge wants the BLE
            // advertised name. `serialNumber` only coincidentally equals it on
            // current records (breaks on legacy ones) and a wrong hint
            // overrides the correct keychain name — pass `deviceName`.
            deviceName: deviceState.deviceName ?? undefined,
          })
        );

        if (!confirmedBindingCode) {
          throw new Error('no_devices_found');
        }

        void syncBindingCodeToServer(confirmedBindingCode, {
          connectedSerial: deviceState.serialNumber ?? deviceState.deviceName,
        });

        logger.info({
          msg: '[settings-reconnect] BLE connection succeeded',
          deviceId: connectionContext.linkedDeviceId,
          serialNumber: deviceState.serialNumber,
          firmwareVersion: deviceState.firmwareVersion,
          batteryLevel: deviceState.batteryLevel,
        });
      } catch (error) {
        if (jotaiStore.get(cancelReconnectPendingAtom)) {
          // User cancelled mid-dial: the queued disconnect owns the outcome,
          // so this failure is expected — no error modal.
          logger.info({
            msg: '[settings-reconnect] attempt settled after cancel — suppressing error',
          });
          return;
        }

        // A manual-reconnect timeout means the IPC reply is late, not that the
        // connect failed: forceReconnect can queue behind an in-flight monitor
        // reconnect arm on the single-lane actor and reply well past our 85s
        // timeout. If the mirrored Rust status still shows progress, clearing
        // device state, surfacing a hard error, or tearing down the scan would
        // all be false negatives — bail and let live Rust state events drive
        // the UI.
        if (isManualReconnectTimeout(error)) {
          const mirroredStatus = jotaiStore.get(
            chronicleDeviceStateAtom
          ).connectionStatus;
          if (RECONNECT_IN_PROGRESS_STATUSES.includes(mirroredStatus)) {
            logger.info({
              msg: '[settings-reconnect] timeout ignored — Rust still progressing',
              status: mirroredStatus,
            });
            return;
          }
        }

        const chronicleError = toChronicleError(parseConnectionError(error));

        // If Rust already restored the background arm, recovery is live and
        // the state events carry the failure reason — for causes the arm can
        // retry through, a terminal modal and a device-state wipe here would
        // contradict the reconnecting pill.
        if (
          ARM_RECOVERABLE_ERROR_CODES.includes(chronicleError.code) &&
          jotaiStore.get(chronicleDeviceStateAtom).connectionStatus ===
            CONNECTION_STATUS.RECONNECTING
        ) {
          logger.info({
            msg: '[settings-reconnect] failure while background arm owns recovery — deferring to state events',
            code: chronicleError.code,
            error: error instanceof Error ? error.message : String(error),
          });
          return;
        }

        logger.error({
          msg: '[settings-reconnect] BLE connection failed',
          deviceId: connectionContext.linkedDeviceId,
          serialNumber: deviceState.serialNumber,
          firmwareVersion: deviceState.firmwareVersion,
          batteryLevel: deviceState.batteryLevel,
          error: error instanceof Error ? error.message : String(error),
        });
        setDeviceState((prev) => ({
          ...prev,
          deviceId: null,
          batteryLevel: null,
          storageTotalMb: null,
          usbModeEnabled: false,
          autoPowerOffMinutes: null,
          isCharging: null,
          recordingPreExisting: false,
        }));
        setErrorWithModal(chronicleError);
        if (isManualReconnectTimeout(error)) {
          // Timed out with no mirrored progress: tear down the orphaned
          // scan/connect on the actor so the next reconnect starts clean.
          void Promise.allSettled([
            chronicleBridge.stopScan(),
            chronicleBridge.disconnect(),
          ]);
        }
      }
    } finally {
      jotaiStore.set(manualReconnectPendingAtom, false);
    }
  }, [
    checkBluetoothStatus,
    clearAutoReconnect,
    connectionContext.linkedDeviceId,
    setConnectionStatus,
    setErrorWithModal,
    setDeviceState,
    deviceState.deviceName,
    deviceState.serialNumber,
    deviceState.firmwareVersion,
    deviceState.batteryLevel,
    jotaiStore,
  ]);

  const disconnect = useCallback(async () => {
    if (!chronicleBridge.isAvailable()) return;

    try {
      markManualDisconnect();
      await chronicleBridge.disconnect();

      track({
        event_name: TrackingEventName.HardwareDeviceDisconnected,
        props: {
          device_id: connectionContext.linkedDeviceId ?? undefined,
          serial_number: deviceState.serialNumber ?? undefined,
          disconnect_reason: 'manual',
        },
      });
      logHardwareDatadogEvent(TrackingEventName.HardwareDeviceDisconnected, {
        device_id: connectionContext.linkedDeviceId,
        serial_number: deviceState.serialNumber,
        disconnect_reason: 'manual',
      });
    } catch (error) {
      logger.error({
        msg: 'Chronicle disconnect failed',
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [connectionContext.linkedDeviceId, deviceState.serialNumber, markManualDisconnect]);

  // Cancelling an auto-reconnect is not a device disconnect — the BLE link is
  // already down — so it must not emit HardwareDeviceDisconnected analytics.
  const cancelReconnect = useCallback(async () => {
    if (!chronicleBridge.isAvailable()) return;
    if (jotaiStore.get(cancelReconnectPendingAtom)) return;
    jotaiStore.set(cancelReconnectPendingAtom, true);

    try {
      markManualDisconnect();
      await chronicleBridge.disconnect();

      logHardwareDatadogEvent(TrackingEventName.HardwareDeviceDisconnected, {
        device_id: connectionContext.linkedDeviceId,
        serial_number: deviceState.serialNumber,
        disconnect_reason: 'reconnect_cancelled',
      });
    } catch (error) {
      logger.error({
        msg: 'Chronicle reconnect cancel failed',
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      jotaiStore.set(cancelReconnectPendingAtom, false);
    }
  }, [
    connectionContext.linkedDeviceId,
    deviceState.serialNumber,
    markManualDisconnect,
    jotaiStore,
  ]);

  return {
    reconnect,
    disconnect,
    cancelReconnect,
    isConnecting,
  };
}
